import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { SESSION_EXPIRED_ERROR_MESSAGE } from '@/services/api'
import { availabilityService } from '@/services/AvailabilityService'
import { companiesService } from '@/services/CompaniesService'
import { sanitizeErrorMessage } from '@/utils/errorSanitizer'

export const useAppStore = defineStore('app', () => {
  // State
  // `business` solo conserva la configuración de disponibilidad; la identidad
  // del negocio vive en authStore.business (la API la llena ahí)
  const business = ref({
    name: '',
    settings: {
      workingHours: {
        start: 9,
        end: 18,
        lunchBreak: {
          start: 13,
          end: 14
        },
        workingDays: [1, 2, 3, 4, 5, 6], // Monday to Saturday
        // Nueva configuración detallada por días
        weeklySchedule: {
          sunday: { available: false, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          monday: { available: true, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          tuesday: { available: true, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          wednesday: { available: true, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          thursday: { available: true, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          friday: { available: true, startTime: '09:00', endTime: '17:00', additionalSlots: [] },
          saturday: { available: false, startTime: '09:00', endTime: '17:00', additionalSlots: [] }
        },
        timezone: 'GMT-06:00',
        repetitionPattern: 'weekly',
        bookingWindow: {
          min: 0,   // días de anticipación mínima
          max: '' // horas hacia adelante (vacío = sin límite)
        },
        /** Minutos entre horarios; se carga de /company-availability/settings (30 mientras no llega) */
        slotIntervalMinutes: 30,
        /** Duración estándar de cada cita en minutos (para reservas y altura visual) */
        appointmentDurationMinutes: 60
      }
    }
  })

  const notifications = ref([])

  const errors = ref([])

  // Getters
  const workingHours = computed(() =>
    business.value.settings.workingHours
  )

  function updateAvailabilityConfig(config) {
    // Actualizar la configuración de disponibilidad detallada (por ahora solo en memoria/localStorage del negocio).
    // Para persistir en backend: llamar aquí a API tipo PUT /companies/:id/schedule o PATCH con config.
    const weekDays = Array.isArray(config.weekDays) ? config.weekDays : []
    business.value.settings.workingHours = {
      ...business.value.settings.workingHours,
      weeklySchedule: weekDays.reduce((schedule, day) => {
        const slots = Array.isArray(day.additionalSlots)
          ? day.additionalSlots.map((s) => ({
              startTime: String(s?.startTime || '09:00').slice(0, 5),
              endTime: String(s?.endTime || '17:00').slice(0, 5),
              capacity: Number(s?.capacity) || 1,
              note: s?.note || '',
              sortOrder: s?.sortOrder ?? 1
            }))
          : []
        schedule[day.key] = {
          available: !!day.available,
          startTime: String(day.startTime || '09:00').slice(0, 5),
          endTime: String(day.endTime || '17:00').slice(0, 5),
          capacity: Number(day.capacity) || 1,
          note: day.note || '',
          additionalSlots: slots
        }
        return schedule
      }, {}),
      timezone: config.timezone,
      repetitionPattern: config.repetitionPattern,
      bookingWindow: config.bookingWindow,
      slotIntervalMinutes: Math.min(120, Math.max(5, Number(config.slotIntervalMinutes) || 30)),
      appointmentDurationMinutes: Math.min(240, Math.max(5, Number(config.appointmentDurationMinutes) || 60))
    }
    
    // También actualizar los arrays legacy para compatibilidad
    const availableDays = weekDays
      .filter(day => day.available)
      .map(day => {
        const dayMap = {
          sunday: 0, monday: 1, tuesday: 2, wednesday: 3, 
          thursday: 4, friday: 5, saturday: 6
        }
        return dayMap[day.key]
      })
    
    business.value.settings.workingHours.workingDays = availableDays

    const wh = business.value.settings.workingHours
    const mins = []
    weekDays.forEach((day) => {
      if (!day.available) return
      const pushRange = (s, e) => {
        const [sh, sm] = s.split(':').map(Number)
        const [eh, em] = e.split(':').map(Number)
        mins.push(sh * 60 + sm, eh * 60 + em)
      }
      pushRange(day.startTime, day.endTime)
      ;(day.additionalSlots || []).forEach((sl) => pushRange(sl.startTime, sl.endTime))
    })
    if (mins.length) {
      wh.start = Math.floor(Math.min(...mins) / 60)
      wh.end = Math.ceil(Math.max(...mins) / 60)
    }

    return business.value.settings.workingHours
  }

  function getDaySchedule(dayKey) {
    return business.value.settings.workingHours.weeklySchedule[dayKey] || null
  }

  // Obtener los días de la semana que están disponibles (0=domingo, 1=lunes, etc.)
  function getAvailableWeekDays() {
    const weeklySchedule = business.value.settings.workingHours.weeklySchedule
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    
    return dayMap
      .map((dayKey, index) => ({
        dayIndex: index,
        dayKey: dayKey,
        available: weeklySchedule[dayKey]?.available || false
      }))
      .filter(day => day.available)
      .map(day => day.dayIndex)
  }

  // Verificar si un día específico está disponible
  function isDayAvailable(date) {
    const dayOfWeek = date.getDay()
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const dayKey = dayMap[dayOfWeek]
    
    const daySchedule = getDaySchedule(dayKey)
    return daySchedule?.available || false
  }

  function getAvailableHoursForDay(date) {
    const dayOfWeek = date.getDay()
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const dayKey = dayMap[dayOfWeek]
    
    const daySchedule = getDaySchedule(dayKey)
    
    if (!daySchedule || !daySchedule.available) {
      return []
    }
    
    const hours = []
    const step =
      Math.min(120, Math.max(5, business.value.settings.workingHours.slotIntervalMinutes || 30)) || 30

    const pushRangeSlots = (startTime, endTime) => {
      const startHour = parseInt(startTime.split(':')[0], 10)
      const startMinute = parseInt(startTime.split(':')[1], 10)
      const endHour = parseInt(endTime.split(':')[0], 10)
      const endMinute = parseInt(endTime.split(':')[1], 10)
      const startM = startHour * 60 + startMinute
      let cur = startM
      const endM = endHour * 60 + endMinute
      while (cur < endM) {
        const h = Math.floor(cur / 60)
        const m = cur % 60
        const timeString = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
        if (!hours.includes(timeString)) hours.push(timeString)
        cur += step
      }
      // Incluir la hora de fin como slot si cae justo en el intervalo (ej. fin 11:30 con paso 30 → slot 11:30)
      if (endM > startM && (endM - startM) % step === 0) {
        const h = Math.floor(endM / 60)
        const m = endM % 60
        const timeString = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
        if (!hours.includes(timeString)) hours.push(timeString)
      }
    }

    const extraSlots = Array.isArray(daySchedule.additionalSlots) ? daySchedule.additionalSlots : []

    if (extraSlots.length > 0) {
      // Múltiples bloques: usar solo los bloques reales, ignorar startTime/endTime primario
      for (const slot of extraSlots) {
        const start = slot?.startTime || slot?.start_time
        const end = slot?.endTime || slot?.end_time
        if (start && end) pushRangeSlots(start, end)
      }
    } else {
      const startTime = daySchedule.startTime && String(daySchedule.startTime).includes(':')
        ? String(daySchedule.startTime).slice(0, 5)
        : '09:00'
      const endTime = daySchedule.endTime && String(daySchedule.endTime).includes(':')
        ? String(daySchedule.endTime).slice(0, 5)
        : '17:00'
      pushRangeSlots(startTime, endTime)
    }

    return hours.sort()
  }

  function getCapacityForDayTime(date, time) {
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const daySchedule = getDaySchedule(dayMap[date.getDay()])
    if (!daySchedule?.available) return 0

    const toMinutes = (value) => {
      const [hour, minute = 0] = String(value || '').slice(0, 5).split(':').map(Number)
      return Number.isFinite(hour) && Number.isFinite(minute) ? (hour * 60) + minute : null
    }
    const target = toMinutes(time)
    if (target == null) return 0

    const configuredBlocks = Array.isArray(daySchedule.additionalSlots) && daySchedule.additionalSlots.length
      ? daySchedule.additionalSlots
      : [daySchedule]

    const block = configuredBlocks.find((candidate) => {
      const start = toMinutes(candidate?.startTime ?? candidate?.start_time)
      const end = toMinutes(candidate?.endTime ?? candidate?.end_time)
      return start != null && end != null && target >= start && target < end
    })

    return block ? Math.max(1, Number(block.capacity) || 1) : 0
  }

  // ─── Availability API state ───────────────────────────────────────
  const availabilityOverrides = ref([])
  const availabilityLoading = ref(false)

  const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

  /**
   * Resetea el estado de disponibilidad a defaults.
   * Se llama antes de cada fetch para evitar datos de otra empresa.
   */
  function resetAvailabilityState() {
    availabilityOverrides.value = []
    business.value.settings.workingHours.weeklySchedule = {
      sunday: { available: false, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      monday: { available: true, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      tuesday: { available: true, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      wednesday: { available: true, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      thursday: { available: true, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      friday: { available: true, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] },
      saturday: { available: false, startTime: '09:00', endTime: '17:00', capacity: 1, note: '', additionalSlots: [] }
    }
    business.value.settings.workingHours.timezone = 'America/Mexico_City'
    business.value.settings.workingHours.bookingWindow = { min: 0, max: '' }
  }

  /**
   * Carga settings + weekly + overrides desde la API y sincroniza el estado local.
   * Se llama al abrir el modal de horarios.
   * Resetea el estado primero para evitar datos de otra empresa (multi-tenant).
   */
  async function fetchAvailability() {
    if (!companiesService.hasActiveCompany()) {
      resetAvailabilityState()
      availabilityLoading.value = false
      return
    }
    availabilityLoading.value = true
    resetAvailabilityState()
    try {
      const [settingsRes, weeklyRes, overridesRes] = await Promise.all([
        availabilityService.getSettings().catch(() => null),
        availabilityService.getWeekly().catch(() => null),
        availabilityService.getOverrides().catch(() => null)
      ])

      const settings = settingsRes?.data ?? settingsRes
      // La rejilla de la agenda sigue el mismo intervalo que usa el backend para ofrecer horas.
      business.value.settings.workingHours.slotIntervalMinutes =
        Math.min(120, Math.max(5, Number(settings?.slot_interval_minutes) || 30))
      const weekly = weeklyRes?.data ?? weeklyRes
      const overrides = overridesRes?.data ?? overridesRes

      if (settings) {
        if (settings.timezone) {
          business.value.settings.workingHours.timezone = settings.timezone
        }
        if (settings.min_booking_days != null) {
          business.value.settings.workingHours.bookingWindow = {
            ...business.value.settings.workingHours.bookingWindow,
            min: settings.min_booking_days
          }
        }
        if ('max_booking_hours_ahead' in settings) {
          business.value.settings.workingHours.bookingWindow = {
            ...business.value.settings.workingHours.bookingWindow,
            max: settings.max_booking_hours_ahead ?? null
          }
        }
      }

      // weekly can be { days: [...] } or directly [...] depending on response shape
      const weeklyDays = Array.isArray(weekly) ? weekly : weekly?.days
      if (weeklyDays && Array.isArray(weeklyDays)) {
        const schedule = {}
        weeklyDays.forEach((day) => {
          const key = DAY_KEYS[day.weekday % 7]
          if (!key) return
          const blocks = Array.isArray(day.blocks) ? day.blocks : []
          const sorted = [...blocks].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

          // Normalize "HH:MM:SS" from backend to "HH:MM" for frontend selects
          const t = (v) => v ? String(v).slice(0, 5) : null

          if (sorted.length <= 1) {
            const primary = sorted[0] || null
            schedule[key] = {
              available: !!day.is_open,
              startTime: t(primary?.start_time) ?? '09:00',
              endTime: t(primary?.end_time) ?? '17:00',
              capacity: primary?.capacity ?? 1,
              workerIds: primary?.worker_ids || primary?.workers?.map((worker) => worker.id) || [],
              note: primary?.note ?? '',
              additionalSlots: []
            }
          } else {
            schedule[key] = {
              available: !!day.is_open,
              startTime: '09:00',
              endTime: '17:00',
              capacity: 1,
              note: '',
              additionalSlots: sorted.map((b, i) => ({
                startTime: t(b.start_time) || '09:00',
                endTime: t(b.end_time) || '17:00',
                capacity: b.capacity ?? 1,
                workerIds: b.worker_ids || b.workers?.map((worker) => worker.id) || [],
                note: b.note ?? '',
                sortOrder: b.sort_order ?? i + 1
              }))
            }
          }
        })
        business.value.settings.workingHours.weeklySchedule = schedule

        const availableDays = weeklyDays
          .filter((d) => d.is_open)
          .map((d) => d.weekday % 7)
        business.value.settings.workingHours.workingDays = availableDays
      }

      const overridesList = Array.isArray(overrides) ? overrides : overrides?.overrides || []
      if (overridesList.length > 0) {
        availabilityOverrides.value = overridesList
      }
    } catch (error) {
      console.error('[AppStore] fetchAvailability:', error)
    } finally {
      availabilityLoading.value = false
    }
  }

  /**
   * Guarda settings (timezone + booking window) en la API.
   */
  async function saveAvailabilitySettings(payload) {
    const data = await availabilityService.updateSettings(payload)
    const result = data?.data ?? data
    if (result?.timezone) {
      business.value.settings.workingHours.timezone = result.timezone
    }
    return data
  }

  /**
   * Guarda el horario semanal en la API.
   * Recibe el array de `days` con formato del backend.
   */
  async function saveAvailabilityWeekly(payload) {
    const data = await availabilityService.updateWeekly(payload)
    return data
  }

  async function createAvailabilityOverride(payload) {
    const { data, httpStatus } = await availabilityService.createOverride(payload)
    const created = data?.data ?? data
    if (created?.id) {
      availabilityOverrides.value.push(created)
    }
    return { data: created, httpStatus }
  }

  async function updateAvailabilityOverride(id, payload) {
    const data = await availabilityService.updateOverride(id, payload)
    const updated = data?.data ?? data
    const idx = availabilityOverrides.value.findIndex((o) => o.id === id)
    if (idx !== -1 && updated) {
      availabilityOverrides.value.splice(idx, 1, updated)
    }
    return data
  }

  async function deleteAvailabilityOverride(id) {
    await availabilityService.deleteOverride(id)
    availabilityOverrides.value = availabilityOverrides.value.filter((o) => o.id !== id)
  }

  function addNotification(notification) {
    const newNotification = {
      id: Date.now(),
      ...notification,
      isRead: false,
      createdAt: new Date().toISOString()
    }
    
    notifications.value.unshift(newNotification)
    return newNotification
  }

  const ERROR_TTL_MS = 5000
  const SUCCESS_TTL_MS = 4000

  function addError(message) {
    // Silenciar cualquier error que derive de sesión expirada (401 during logout or refresh)
    if (!message || typeof message !== 'string') return
    if (message.includes(SESSION_EXPIRED_ERROR_MESSAGE)) return

    const text = sanitizeErrorMessage(message)
    // Reintentar la misma acción no apila toasts: reaparece recién cuando el anterior venció.
    if (errors.value.some((e) => e.message === text)) return

    const error = {
      id: Date.now() + Math.random(),
      message: text,
      duration: ERROR_TTL_MS,
      timestamp: new Date().toISOString()
    }

    errors.value.push(error)

    setTimeout(() => {
      removeError(error.id)
    }, ERROR_TTL_MS)
  }

  function removeError(errorId) {
    const index = errors.value.findIndex(e => e.id === errorId)
    if (index !== -1) {
      errors.value.splice(index, 1)
    }
  }

  function clearErrors() {
    errors.value = []
  }

  const successes = ref([])

  function addSuccess(message) {
    if (!message || typeof message !== 'string') return
    if (successes.value.some((s) => s.message === message)) return
    const item = { id: Date.now() + Math.random(), message, duration: SUCCESS_TTL_MS }
    successes.value.push(item)
    setTimeout(() => removeSuccess(item.id), SUCCESS_TTL_MS)
  }

  function removeSuccess(id) {
    const index = successes.value.findIndex(s => s.id === id)
    if (index !== -1) successes.value.splice(index, 1)
  }

  // Reset
  function $reset() {
    notifications.value = []
    errors.value = []
  }

  const companyChangeKey = ref(0)
  function triggerCompanyChange() { companyChangeKey.value++ }

  return {
    // State
    companyChangeKey,
    business,
    notifications,
    errors,

    // Getters
    workingHours,

    // Actions
    updateAvailabilityConfig,
    getDaySchedule,
    getAvailableWeekDays,
    isDayAvailable,
    getAvailableHoursForDay,
    getCapacityForDayTime,
    availabilityOverrides,
    availabilityLoading,
    resetAvailabilityState,
    triggerCompanyChange,
    fetchAvailability,
    saveAvailabilitySettings,
    saveAvailabilityWeekly,
    createAvailabilityOverride,
    updateAvailabilityOverride,
    deleteAvailabilityOverride,
    addNotification,
    addError,
    removeError,
    clearErrors,
    successes,
    addSuccess,
    removeSuccess,
    $reset
  }
})
